import React, { useState, useEffect, useCallback } from 'react';
import { Box, Grid, CircularProgress, Typography, TextField, IconButton } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import KpiPanel from './KpiPanel';
import LlmCostChart from './LlmCostChart';
import LlmCostByTypeChart from './LlmCostByTypeChart';
import VersionAdoptionTable from './VersionAdoptionTable';
import ChatUsageTable from '../Chat/ChatUsageTable';
import { getLlmCosts, getLlmCostsByType, getSubscribers, getChatUsage, getVersionAdoption, getAvgCostPerSubscriber, getAvgDailyCostPerSubscriber, getDailySubscriberUsage, getTotalCost, getUniqueSubscribers } from '../../utils/bigQuery';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';

function Dashboard() {
  const [pastDays, setPastDays] = useState(7);
  const [inputValue, setInputValue] = useState("7");
  const [stats, setStats] = useState({
    activeSubscribers: 0,
    appleSubscribers: 0,
    googleSubscribers: 0,
    yearlySubscribers: 0,
    monthlySubscribers: 0,
    totalPanelRequests: 0,
    totalChatRequests: 0,
    totalTokenCount: 0,
    averageRequestsPerDay: 0,
    newSubscriptions: 0,
    cancellations: 0,
    renewals: 0,
    llmCosts: [],
    llmCostsByType: []
  });
  const [loading, setLoading] = useState(true);
  const [avgCostPerSubscriber, setAvgCostPerSubscriber] = useState(null);
  const [dailySubscriberUsage, setDailySubscriberUsage] = useState(null);
  const [uniqueSubscribers, setUniqueSubscribers] = useState(null);
  const [totalCost, setTotalCost] = useState(null);
  const [chatUsage, setChatUsage] = useState([]);
  const [avgDailyCostPerUser, setAvgDailyCostPerUser] = useState(null);
  const [versionAdoption, setVersionAdoption] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'usage_1', direction: 'desc' });

  const getSortableValue = (row, key) => {
    if (key === 'promptName') return row.promptName || '';
    const [type, interval] = key.split('_');
    if (type === 'usage') return row.intervals[interval]?.usage ?? 0;
    if (type === 'cost') return row.intervals[interval]?.totalCost ?? 0;
    if (type === 'tokens') return row.intervals[interval]?.totalTokenCount ?? 0;
    return '';
  };

  const sortedChatUsage = [...chatUsage].sort((a, b) => {
    const { key, direction } = sortConfig;
    const aValue = getSortableValue(a, key);
    const bValue = getSortableValue(b, key);
    if (typeof aValue === 'string') {
      return direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    return direction === 'asc'
      ? aValue - bValue
      : bValue - aValue;
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  // Debounced function to update pastDays
  const debouncedSetPastDays = useCallback(
    (() => {
      let timeoutId;
      return (value) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          const days = parseInt(value, 10);
          if (!isNaN(days) && days > 0) {
            setLoading(true);
            setPastDays(days);
          }
        }, 500); // 500ms delay
      };
    })(),
    []
  );

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSetPastDays(value);
  };

  const fetchStats = useCallback(async () => {
    try {
      const date = new Date();
      date.setDate(date.getDate() - pastDays);

      // Fetch LLM costs, costs by type, avg cost per subscriber, avg daily cost per user, daily subscriber usage, total cost, and unique subscribers
      const [llmCosts, llmCostsByType, avgCostResp, avgDailyCostResp, dailyUsageResp, totalCostResp, uniqueSubscribersResp] = await Promise.all([
        getLlmCosts(pastDays),
        getLlmCostsByType(pastDays),
        getAvgCostPerSubscriber(pastDays),
        getAvgDailyCostPerSubscriber(pastDays),
        getDailySubscriberUsage(pastDays),
        getTotalCost(pastDays),
        getUniqueSubscribers(pastDays)
      ]);

      // Get active subscribers from the API
      const { subscribers } = await getSubscribers();
      const activeSubscribers = subscribers.length;
      const appleSubscribers = subscribers.filter(sub => sub.store === 'Apple').length;
      const googleSubscribers = subscribers.filter(sub => sub.store === 'Google').length;
      const yearlySubscribers = subscribers.filter(sub => sub.subscriptionFrequency === 'Yearly').length;
      const monthlySubscribers = subscribers.filter(sub => sub.subscriptionFrequency === 'Monthly').length;

      // Calculate total panel requests as the sum of requestCount where type is 'PANEL'
      const totalPanelRequests = llmCostsByType.reduce((sum, item) => sum + (item.requestType === 'PANEL' ? item.requestCount || 0 : 0), 0);

      // Calculate total token count from llm costs
      const totalTokenCount = llmCosts.reduce((sum, item) => sum + (item.totalTokens || 0), 0);

      // Calculate average requests per day based on llmCostsByType data
      const uniqueDates = new Set(llmCostsByType.map(item => item.date));
      const averageRequestsPerDay = uniqueDates.size > 0 ? Math.round(totalPanelRequests / uniqueDates.size) : 0;

      // Calculate cutoff date for time-based filters
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - pastDays);
      const cutoffDateUTC = cutoffDate.toISOString();

      // Calculate new subscriptions (subscribers with INITIAL_PURCHASE within pastDays)
      const newSubscriptions = subscribers.filter(sub => 
        sub.lastEvent === 'INITIAL_PURCHASE' && 
        sub.lastEventDate >= cutoffDateUTC
      ).length;

      // Calculate cancellations (subscribers with CANCELLATION within pastDays)
      const cancellations = subscribers.filter(sub =>
        sub.lastEvent === 'CANCELLATION' &&
        sub.lastEventDate >= cutoffDateUTC
      ).length;

      // Calculate total chat requests and count of chat/panel requests
      const totalChatRequests = llmCostsByType.reduce((sum, item) => sum + (item.requestType === 'CHAT' ? item.requestCount || 0 : 0), 0);

      // Calculate renewals within pastDays
      const renewals = subscribers.filter(sub =>
        sub.lastEvent === 'RENEWAL' &&
        sub.lastEventDate >= cutoffDateUTC
      ).length;

      setStats({
        activeSubscribers,
        appleSubscribers,
        googleSubscribers,
        yearlySubscribers,
        monthlySubscribers,
        totalPanelRequests,
        totalTokenCount,
        averageRequestsPerDay,
        llmCosts,
        llmCostsByType,
        totalChatRequests,
        newSubscriptions,
        cancellations,
        renewals
      });
      setAvgCostPerSubscriber(avgCostResp.averageCostPerUser);
      setAvgDailyCostPerUser(avgDailyCostResp.averageDailyCostPerUser);
      setDailySubscriberUsage(dailyUsageResp.dailySubscriberUsage);
      setTotalCost(totalCostResp.totalCost);
      setUniqueSubscribers(uniqueSubscribersResp.distinctSubscribers);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setAvgCostPerSubscriber(null);
      setAvgDailyCostPerUser(null);
      setDailySubscriberUsage(null);
    } finally {
      setLoading(false);
    }
  }, [pastDays]);

  const handleRefresh = () => {
    setLoading(true);
    fetchStats();
    // Refresh version adoption data
    getVersionAdoption(pastDays)
      .then((data) => setVersionAdoption(data.versionAdoption || []))
      .catch((err) => {
        console.error('Error fetching version adoption:', err);
        setVersionAdoption([]);
      });
  };

  useEffect(() => {
    fetchStats();
    // Fetch chat usage stats
    getChatUsage()
      .then(setChatUsage)
      .catch((err) => {
        console.error('Error fetching chat usage:', err);
        setChatUsage([]);
      });
    // Fetch version adoption stats
    getVersionAdoption(pastDays)
      .then((data) => setVersionAdoption(data.versionAdoption || []))
      .catch((err) => {
        console.error('Error fetching version adoption:', err);
        setVersionAdoption([]);
      });
  }, [fetchStats, pastDays]);

  const dashboardContent = (
    <Box sx={{ 
      width: '100%',
      p: 3,
      '& > *:not(:last-child)': {
        mb: 3
      }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
        <Typography variant="h4">
          Results for Prior
        </Typography>
        <TextField
          value={inputValue}
          onChange={handleInputChange}
          type="number"
          variant="outlined"
          size="small"
          sx={{ width: 100 }}
          inputProps={{ 
            min: 1,
            style: { textAlign: 'center' }
          }}
        />
        <Typography variant="h4">
          Days
        </Typography>
        </Box>
        <IconButton 
          onClick={handleRefresh}
          disabled={loading}
          title="Refresh dashboard"
          aria-label="Refresh dashboard"
          color="primary"
          size="medium"
          sx={{ width: 'auto' }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>
      <Grid container spacing={3} sx={{ width: '100%' }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiPanel
            title="All Subscribers"
            width='300px'
            value={stats.activeSubscribers.toLocaleString()}
            sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiPanel
            title="Apple / Google Subscribers"
            width='300px'
            value={`${stats.appleSubscribers.toLocaleString()} / ${stats.googleSubscribers.toLocaleString()}`}
            sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiPanel
            title="Yearly / Monthly Subscribers"
            width='300px'
            value={`${stats.yearlySubscribers.toLocaleString()} / ${stats.monthlySubscribers.toLocaleString()}`}
            sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ width: '100%' }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiPanel
            title="Unique Subscribers"
            width='300px'
            value={uniqueSubscribers !== null ? uniqueSubscribers.toLocaleString() : 'Loading...'}
            sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiPanel
            title="Avg. Daily Subscribers"
            width='300px'
            value={dailySubscriberUsage !== null ? dailySubscriberUsage.toLocaleString(undefined, { maximumFractionDigits: 2 }) : 'Loading...'}
            sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiPanel
            title="Total Cost"
            width='300px'
            value={totalCost !== null ? totalCost.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'Loading...'}
            sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiPanel
            title="Total Cost per Subscriber"
            width='300px'
            value={avgCostPerSubscriber !== null ? avgCostPerSubscriber.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'Loading...'}
            sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ width: '100%' }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiPanel
            title="New Subscriptions"
            width='300px'
            value={stats.newSubscriptions.toLocaleString()}
            sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiPanel
            title="Renewals"
            width='300px'
            value={stats.renewals.toLocaleString()}
            sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiPanel
            title="Cancellations"
            width='300px'
            value={stats.cancellations.toLocaleString()}
            sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ width: '100%' }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiPanel
            title="Total Panel Requests"
            width='300px'
            value={stats.totalPanelRequests.toLocaleString()}
            sx={{ bgcolor: 'secondary.light', color: 'secondary.contrastText' }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiPanel
            title="Total Chat Requests"
            width='300px'
            value={stats.totalChatRequests.toLocaleString()}
            sx={{ bgcolor: 'grey.400', color: 'grey.900' }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiPanel
            title="Total Token Count"
            width='300px'
            value={stats.totalTokenCount.toLocaleString()}
            sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiPanel
            title="Average Requests per Day"
            width='300px'
            value={stats.averageRequestsPerDay.toLocaleString()}
            sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}
          />
        </Grid>
      </Grid>
      <Box sx={{ width: '100%', mb: 3, flexGrow: 1 }}>
<KpiPanel
  title={`Version Adoption (Last ${pastDays} Days)`}
  width="100%"
  value={<VersionAdoptionTable versionAdoption={versionAdoption} />}
  sx={{ bgcolor: 'grey.100', height: '100%' }}
/>
      </Box>
      <Box sx={{ width: '100%', mb: 3, flexGrow: 1 }}>
<KpiPanel
  title="Chat Usage by Prompt"
  width="100%"
  value={
    <ChatUsageTable
      sortedChatUsage={sortedChatUsage}
      sortConfig={sortConfig}
      handleSort={handleSort}
    />
  }
  sx={{ bgcolor: 'grey.100', height: '100%' }}
/>
      </Box>

      <Box sx={{ width: '100%', mb: 3, flexGrow: 1 }}>
        <KpiPanel
          title="Daily LLM Costs"
          width='100%'
          chartData={stats.llmCosts}
          Chart={LlmCostChart}
          sx={{ 
            bgcolor: 'grey.100', 
            height: '100%',
            '& .recharts-responsive-container': {
              width: '100% !important'
            }
          }}
        />
      </Box>
      <Box sx={{ width: '100%', flexGrow: 1 }}>
        <KpiPanel
          title="Daily LLM Costs by Request Type"
          width='100%'
          chartData={stats.llmCostsByType}
          Chart={LlmCostByTypeChart}
          sx={{ 
            bgcolor: 'grey.100', 
            height: '100%',
            '& .recharts-responsive-container': {
              width: '100% !important'
            }
          }}
        />
      </Box>
    </Box>
  );

  return (
    <Box position="relative">
      {dashboardContent}
      {loading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            zIndex: 1000,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}

export default Dashboard;
